import { useEffect, useState } from 'react';
import { FaCalendarAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useGetToken } from '../hooks/custom-hooks';
import { getCurrentYearDimEvents, getLogicAppCurrentStatus, reprocessSingleEvent } from '../services/dimension-service';
import { Dropdown } from '../shared/Dropdown';
import Loader from '../navigation/Loader';
import '../styles/modal.css';

export function ReprocessSingleEvent() {
    const token = useGetToken();
    const [isVisible, setIsVisible] = useState(false);
    const [events, setEvents] = useState([]);
    const [selectedEventCode, setSelectedEventCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isVisible || token === null) {
            return;
        }

        const loadEvents = async () => {
            setIsLoading(true);
            setSelectedEventCode('');

            const response = await getCurrentYearDimEvents(token);

            if (response.data) {
                setEvents(response.data
                    .map((event) => ({
                        id: event.eventCode,
                        name: event.eventCode
                    }))
                    .sort((firstEvent, secondEvent) =>
                        firstEvent.name.localeCompare(secondEvent.name, undefined, { sensitivity: 'base' })
                    ));
            } else {
                setEvents([]);
                toast.error('Unable to load current-year events.');
            }

            setIsLoading(false);
        };

        loadEvents();
    }, [isVisible, token]);

    const closePopup = () => {
        setIsVisible(false);
        setSelectedEventCode('');
    };

    const showPopup = async () => {
        const statusResponse = await getLogicAppCurrentStatus(token);

        if (!statusResponse.data) {
            toast.error('Cannot run custom interval. There is a process currently running.');
            return;
        }

        setIsVisible(true);
    };

    const handleReprocess = async () => {
        setIsSubmitting(true);

        const statusResponse = await getLogicAppCurrentStatus(token);

        if (!statusResponse.data) {
            toast.error('Cannot run custom interval. There is a process currently running.');
            setIsSubmitting(false);
            return;
        }

        const response = await reprocessSingleEvent(selectedEventCode, token);

        if (response.error) {
            toast.error('Unable to start event reprocessing.');
        } else {
            toast.success(`${selectedEventCode} reprocessing has been started.`);
            closePopup();
        }

        setIsSubmitting(false);
    };

    return (
        <>
            <div className='module-container-slim' onClick={showPopup}>
                <div className='icon-text-row App-text'>
                    <FaCalendarAlt className='teal-icons'/>
                    Reprocess Single Event
                </div>
            </div>
            {isVisible && (
                <div className='modal' role='dialog' aria-modal='true' aria-labelledby='single-event-reprocess-title'>
                    <div className='modal-content'>
                        <div className='modal-title' id='single-event-reprocess-title'>Reprocess Single Event</div>
                        <div className='modal-body'>
                            <p className='single-event-reprocess__description'>Select the current-year event to reprocess.</p>
                            {isLoading ? (
                                <Loader color='#3fc2cd'/>
                            ) : (
                                <Dropdown
                                    id='singleEventReprocessDropdown'
                                    value={selectedEventCode}
                                    dropdownValues={events}
                                    handleDropdownChange={(event) => setSelectedEventCode(event.target.value)}
                                    label='Event code'
                                    className='form-fieldgroup single-event-reprocess__select'
                                    defaultPhrase='Select an event'
                                />
                            )}
                        </div>
                        <div className='modal-footer'>
                            <button className='button btnCancel' onClick={closePopup} disabled={isSubmitting}>Cancel</button>
                            <button className='button btn1' onClick={handleReprocess} disabled={!selectedEventCode || isLoading || isSubmitting}>
                                {isSubmitting ? 'Reprocessing…' : 'Reprocess'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
